import React, { createContext, useState, useContext, ReactNode } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert, { AlertColor } from '@mui/material/Alert';

// Define the toast context type
interface ToastContextType {
  showToast: (message: string, severity: AlertColor, duration?: number) => void;
  hideToast: () => void;
}

// Create the context with a default value
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Define props for the ToastProvider component
interface ToastProviderProps {
  children: ReactNode;
}

// Toast message interface
interface ToastMessage {
  message: string;
  severity: AlertColor;
  duration: number;
}

// Create the ToastProvider component
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<ToastMessage>({
    message: '',
    severity: 'info',
    duration: 6000,
  });

  // Function to show a toast
  const showToast = (message: string, severity: AlertColor = 'info', duration = 6000) => {
    setToast({ message, severity, duration });
    setOpen(true);
  };

  // Function to hide the toast
  const hideToast = () => {
    setOpen(false);
  };

  // Handle close event
  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={toast.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

// Custom hook to use the toast context
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};